"use client";

import * as React from "react";
import { Renderer, Program, Triangle, Mesh } from "ogl";

export type RaysOrigin =
  | "top-center"
  | "top-left"
  | "top-right"
  | "right"
  | "left"
  | "bottom-center"
  | "bottom-right"
  | "bottom-left";

type Vec2 = [number, number];
type Vec3 = [number, number, number];

interface Uniforms {
  iTime: { value: number };
  iResolution: { value: Vec2 };
  rayPos: { value: Vec2 };
  rayDir: { value: Vec2 };
  raysColor: { value: Vec3 };
  raysSpeed: { value: number };
  lightSpread: { value: number };
  rayLength: { value: number };
  pulsating: { value: number };
  fadeDistance: { value: number };
  saturation: { value: number };
  mousePos: { value: Vec2 };
  mouseInfluence: { value: number };
  noiseAmount: { value: number };
  distortion: { value: number };
}

interface LightRaysProps {
  raysOrigin?: RaysOrigin;
  raysColor?: string;
  raysSpeed?: number;
  lightSpread?: number;
  rayLength?: number;
  pulsating?: boolean;
  fadeDistance?: number;
  saturation?: number;
  followMouse?: boolean;
  mouseInfluence?: number;
  noiseAmount?: number;
  distortion?: number;
  className?: string;
}

const DEFAULT_COLOR = "#ffffff";

const hexToRgb = (hex: string): Vec3 => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m
    ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255]
    : [1, 1, 1];
};

const getAnchorAndDir = (origin: RaysOrigin, w: number, h: number) => {
  const outside = 0.2;
  switch (origin) {
    case "top-left":
      return { anchor: [0, -outside * h] as Vec2, dir: [0, 1] as Vec2 };
    case "top-right":
      return { anchor: [w, -outside * h] as Vec2, dir: [0, 1] as Vec2 };
    case "left":
      return { anchor: [-outside * w, 0.5 * h] as Vec2, dir: [1, 0] as Vec2 };
    case "right":
      return { anchor: [(1 + outside) * w, 0.5 * h] as Vec2, dir: [-1, 0] as Vec2 };
    case "bottom-left":
      return { anchor: [0, (1 + outside) * h] as Vec2, dir: [0, -1] as Vec2 };
    case "bottom-center":
      return { anchor: [0.5 * w, (1 + outside) * h] as Vec2, dir: [0, -1] as Vec2 };
    case "bottom-right":
      return { anchor: [w, (1 + outside) * h] as Vec2, dir: [0, -1] as Vec2 };
    default:
      return { anchor: [0.5 * w, -outside * h] as Vec2, dir: [0, 1] as Vec2 };
  }
};

export default function LightRays({
  raysOrigin = "top-center",
  raysColor = DEFAULT_COLOR,
  raysSpeed = 1,
  lightSpread = 1,
  rayLength = 2,
  pulsating = false,
  fadeDistance = 1.0,
  saturation = 1.0,
  followMouse = true,
  mouseInfluence = 0.1,
  noiseAmount = 0.0,
  distortion = 0.0,
  className = "",
}: LightRaysProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const uniformsRef = React.useRef<Uniforms | null>(null);
  const rendererRef = React.useRef<Renderer | null>(null);
  const meshRef = React.useRef<Mesh | null>(null);

  const mouseRef = React.useRef({ x: 0.5, y: 0.5 });
  const smoothMouseRef = React.useRef({ x: 0.5, y: 0.5 });

  const rafRef = React.useRef<number | null>(null);
  const observerRef = React.useRef<IntersectionObserver | null>(null);

  const [isVisible, setIsVisible] = React.useState(false);
  const [isRunning, setIsRunning] = React.useState(true);

  // ✅ Pausa real cuando la pestaña está oculta
  React.useEffect(() => {
    const onVis = () => setIsRunning(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // ✅ Visible only when in viewport
  React.useEffect(() => {
    if (!containerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => setIsVisible(entries[0]?.isIntersecting ?? false),
      { threshold: 0.1 }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  // ✅ Init WebGL UNA sola vez (cuando entra al viewport)
  React.useEffect(() => {
    if (!isVisible || !containerRef.current) return;
    if (rendererRef.current || uniformsRef.current || meshRef.current) return;

    const container = containerRef.current;

    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 1.5),
      alpha: true,
    });
    rendererRef.current = renderer;

    const gl = renderer.gl;
    gl.canvas.style.width = "100%";
    gl.canvas.style.height = "100%";

    while (container.firstChild) container.removeChild(container.firstChild);
    container.appendChild(gl.canvas);

    const vert = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

    const frag = `precision highp float;

uniform float iTime;
uniform vec2  iResolution;

uniform vec2  rayPos;
uniform vec2  rayDir;
uniform vec3  raysColor;
uniform float raysSpeed;
uniform float lightSpread;
uniform float rayLength;
uniform float pulsating;
uniform float fadeDistance;
uniform float saturation;
uniform vec2  mousePos;
uniform float mouseInfluence;
uniform float noiseAmount;
uniform float distortion;

varying vec2 vUv;

float noise(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
}

float rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord,
                  float seedA, float seedB, float speed) {
  vec2 sourceToCoord = coord - raySource;
  vec2 dirNorm = normalize(sourceToCoord);
  float cosAngle = dot(dirNorm, rayRefDirection);

  float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;
  float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));

  float distance = length(sourceToCoord);
  float maxDistance = iResolution.x * rayLength;
  float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);

  float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);
  float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;

  float baseStrength = clamp(
    (0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) +
    (0.3 + 0.2 * cos(-distortedAngle * seedB + iTime * speed)),
    0.0, 1.0
  );

  return baseStrength * lengthFalloff * fadeFalloff * spreadFactor * pulse;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 coord = vec2(fragCoord.x, iResolution.y - fragCoord.y);

  vec2 finalRayDir = rayDir;
  if (mouseInfluence > 0.0) {
    vec2 mouseScreenPos = mousePos * iResolution.xy;
    vec2 mouseDirection = normalize(mouseScreenPos - rayPos);
    finalRayDir = normalize(mix(rayDir, mouseDirection, mouseInfluence));
  }

  vec4 rays1 = vec4(1.0) *
               rayStrength(rayPos, finalRayDir, coord, 36.2214, 21.11349,
                           1.5 * raysSpeed);
  vec4 rays2 = vec4(1.0) *
               rayStrength(rayPos, finalRayDir, coord, 22.3991, 18.0234,
                           1.1 * raysSpeed);

  fragColor = rays1 * 0.5 + rays2 * 0.4;

  if (noiseAmount > 0.0) {
    float n = noise(coord * 0.01 + iTime * 0.1);
    fragColor.rgb *= (1.0 - noiseAmount + noiseAmount * n);
  }

  float brightness = 1.0 - (coord.y / iResolution.y);
  fragColor.x *= 0.1 + brightness * 0.8;
  fragColor.y *= 0.3 + brightness * 0.6;
  fragColor.z *= 0.5 + brightness * 0.5;

  if (saturation != 1.0) {
    float gray = dot(fragColor.rgb, vec3(0.299, 0.587, 0.114));
    fragColor.rgb = mix(vec3(gray), fragColor.rgb, saturation);
  }

  fragColor.rgb *= raysColor;
}

void main() {
  vec4 color;
  mainImage(color, gl_FragCoord.xy);
  gl_FragColor  = color;
}`;

    const uniforms: Uniforms = {
      iTime: { value: 0 },
      iResolution: { value: [1, 1] },
      rayPos: { value: [0, 0] },
      rayDir: { value: [0, 1] },
      raysColor: { value: hexToRgb(raysColor) },
      raysSpeed: { value: raysSpeed },
      lightSpread: { value: lightSpread },
      rayLength: { value: rayLength },
      pulsating: { value: pulsating ? 1.0 : 0.0 },
      fadeDistance: { value: fadeDistance },
      saturation: { value: saturation },
      mousePos: { value: [0.5, 0.5] },
      mouseInfluence: { value: mouseInfluence },
      noiseAmount: { value: noiseAmount },
      distortion: { value: distortion },
    };

    uniformsRef.current = uniforms;

    const geometry = new Triangle(gl);
    const program = new Program(gl, { vertex: vert, fragment: frag, uniforms });
    meshRef.current = new Mesh(gl, { geometry, program });

    const updatePlacement = () => {
      if (!containerRef.current || !rendererRef.current || !uniformsRef.current) return;

      // ✅ CORRECCIÓN: no subas a 2 si quieres ahorro, mantén 1.5
      rendererRef.current.dpr = Math.min(window.devicePixelRatio, 1.5);

      const { clientWidth: wCSS, clientHeight: hCSS } = containerRef.current;
      rendererRef.current.setSize(wCSS, hCSS);

      const dpr = rendererRef.current.dpr;
      const w = wCSS * dpr;
      const h = hCSS * dpr;

      uniformsRef.current.iResolution.value = [w, h];

      const { anchor, dir } = getAnchorAndDir(raysOrigin, w, h);
      uniformsRef.current.rayPos.value = anchor;
      uniformsRef.current.rayDir.value = dir;
    };

    window.addEventListener("resize", updatePlacement, { passive: true });
    updatePlacement();

    return () => {
      window.removeEventListener("resize", updatePlacement);

      // cleanup total (solo si el componente se desmonta)
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      if (rendererRef.current) {
        try {
          const canvas = rendererRef.current.gl.canvas;
          const lose = rendererRef.current.gl.getExtension("WEBGL_lose_context");
          lose?.loseContext();
          canvas?.parentNode?.removeChild(canvas);
        } catch {}
      }

      rendererRef.current = null;
      uniformsRef.current = null;
      meshRef.current = null;
      rafRef.current = null;
    };
  }, [isVisible]); // ✅ solo depende de visibilidad para inicializar una vez

  // ✅ Actualiza uniforms cuando cambian props (sin reiniciar WebGL)
  React.useEffect(() => {
    if (!uniformsRef.current) return;
    const u = uniformsRef.current;

    u.raysColor.value = hexToRgb(raysColor);
    u.raysSpeed.value = raysSpeed;
    u.lightSpread.value = lightSpread;
    u.rayLength.value = rayLength;
    u.pulsating.value = pulsating ? 1.0 : 0.0;
    u.fadeDistance.value = fadeDistance;
    u.saturation.value = saturation;
    u.mouseInfluence.value = mouseInfluence;
    u.noiseAmount.value = noiseAmount;
    u.distortion.value = distortion;
  }, [
    raysColor,
    raysSpeed,
    lightSpread,
    rayLength,
    pulsating,
    fadeDistance,
    saturation,
    mouseInfluence,
    noiseAmount,
    distortion,
  ]);

  // ✅ Mouse
  React.useEffect(() => {
    if (!followMouse) return;

    const onMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      mouseRef.current = { x, y };
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [followMouse]);

  // ✅ Loop separado: arranca y se detiene según isVisible + isRunning
  React.useEffect(() => {
    if (!isVisible || !isRunning) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }

    const loop = (t: number) => {
      if (!rendererRef.current || !uniformsRef.current || !meshRef.current) return;

      uniformsRef.current.iTime.value = t * 0.001;

      if (followMouse && mouseInfluence > 0.0) {
        const smoothing = 0.92;

        smoothMouseRef.current.x =
          smoothMouseRef.current.x * smoothing + mouseRef.current.x * (1 - smoothing);
        smoothMouseRef.current.y =
          smoothMouseRef.current.y * smoothing + mouseRef.current.y * (1 - smoothing);

        uniformsRef.current.mousePos.value = [smoothMouseRef.current.x, smoothMouseRef.current.y];
      }

      rendererRef.current.render({ scene: meshRef.current });
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isVisible, isRunning, followMouse, mouseInfluence]);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`.trim()}
    />
  );
}
